import { useEffect, useState } from "react"

import { useIntelligentBaselineData } from "~/api/hooks/genability/useIntelligentBaseline"
import type { UseProposal } from "~/containers/Proposal/state/proposal/proposal-context"
import { resolveMonthlyEnergySource } from "~/utils/finance/energy"

import type { MonthData } from "../components/types"

const initialGridData: MonthData[] = Array.from({ length: 12 }, (_, index) => {
  const monthNames = [
    `Jan`,
    `Feb`,
    `Mar`,
    `Apr`,
    `May`,
    `Jun`,
    `Jul`,
    `Aug`,
    `Sep`,
    `Oct`,
    `Nov`,
    `Dec`,
  ]

  return {
    index,
    name: monthNames[index],
    value: null,
    source: `empty`,
    costValue: null,
    kwhValue: null,
    manualEntryUnit: null,
  }
})

export const useEnergyConsumptionInitialization = (
  proposal: ReturnType<UseProposal>,
  accountId: string | undefined,
): {
  monthlyData: MonthData[]
  setMonthlyData: React.Dispatch<React.SetStateAction<MonthData[]>>
} => {
  const [monthlyData, setMonthlyData] = useState<MonthData[]>(initialGridData)
  const [initializationSource, setInitializationSource] = useState<
    `baseline` | `none` | `stored`
  >(`none`)

  const { data: intelligentBaselineData } = useIntelligentBaselineData(accountId)

  // Clean initialization: database first, then intelligent baseline for new proposals
  useEffect(() => {
    const syncMonthlyDataToProposal = (data: MonthData[]) => {
      const monthlyEnergyCost = data.map((month) => month.costValue ?? 0)
      const monthlyEnergyUse = data.map((month) => month.kwhValue ?? 0)
      const monthlyEnergyManualFlags = data.map(
        (month) => month.source === `user`,
      )

      proposal.changeLocal({
        monthlyEnergyCost,
        monthlyEnergyUse,
        monthlyEnergyManualFlags,
      })
    }

    const storedCosts = proposal.local.monthlyEnergyCost
    const storedUsage = proposal.local.monthlyEnergyUse
    const storedManualFlags = proposal.local.monthlyEnergyManualFlags

    // Check if we have saved data from database
    const hasStoredData =
      storedCosts?.some((cost) => cost > 0) ||
      storedUsage?.some((usage) => usage > 0)

    if (hasStoredData && initializationSource !== `stored`) {
      // Priority 1: Use database values - only load once
      setMonthlyData((prev) => {
        const next = prev.map((month, index) => {
          const costValue = storedCosts?.[index] || 0
          const kwhValue = storedUsage?.[index] || 0
          const manualFlag = storedManualFlags?.[index] ?? false

          return {
            ...month,
            costValue,
            kwhValue,
            source: resolveMonthlyEnergySource(costValue, kwhValue, manualFlag),
            manualEntryUnit: null,
          }
        })

        return next
      })
      setInitializationSource(`stored`)
    } else if (
      intelligentBaselineData?.monthlyEstimates &&
      initializationSource === `none`
    ) {
      // Priority 2: Use intelligent baseline for new proposals (only if no stored data)
      setMonthlyData((prev) => {
        const next = prev.map((month) => {
          const estimate = intelligentBaselineData.monthlyEstimates.find(
            (e) => e.month === month.index,
          )
          if (estimate) {
            return {
              ...month,
              costValue: estimate.amount,
              kwhValue: estimate.usage,
              source: `estimated` as const,
              manualEntryUnit: null,
            }
          }
          return month
        })

        syncMonthlyDataToProposal(next)

        return next
      })
      setInitializationSource(`baseline`)
    }
  }, [
    proposal.local.monthlyEnergyCost,
    proposal.local.monthlyEnergyUse,
    intelligentBaselineData,
    initializationSource,
    proposal.local.monthlyEnergyManualFlags,
    proposal.changeLocal,
  ])

  return { monthlyData, setMonthlyData }
}
